import Layout from '@/shared/Layout/ui/Layout';
import Link from 'next/link';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import * as S from './CartPage.styles';

import { Tooltip } from '@/common/ui/containers/Tooltip/Tooltip';
import { useCartContext } from '@/entities/Cart/app/CartContext';
//import { getSavingsFromCartItems } from '@/entities/Cart/app/utils/cartUtils';
import { IApiCartItem } from '@/entities/Cart/domain/Cart.domain';
import { CartItem } from './CartItem/CartItem';
import { CartSad } from './CartSad';
import { ExpiredCart } from './ExpiredCart/ExpiredCart';
import { PromocodeWidget } from './PromocodeWidget/PromocodeWidget';
import CartPageMeta from './app/Meta/Meta';
import { trackUpdateCart } from '@/common/app/utils/iterable';
import { Preloader } from '@/common/ui/loaders/Preloader';
import { AnalyticsInstance } from '@/common/service/analytics';
import { PayPalSection } from '@/common/ui/styled/PayPalSection/PayPalSection';
import { getNormalisedPrice } from '@/common/app/utils/getNormalisedPrice';
import { Question } from '@/shared/images/icons/Question';
import { ContactInfo } from '../CheckoutPage/ui/ContactInfo/ContactInfo';
import BillingInfo from '../CheckoutPage/ui/BillingInfo/BillingInfo';
import { getLS, LocalStorageTypes } from '@/common/service/storage';
import { useRouter } from 'next/router';
import { ShopperApprovedReviews } from '@/shared/ShopperApproved/ShopperApprovedReviews';

const TRIPFLEX_ID = '1';

/* eslint-disable sonarjs/cognitive-complexity */
const CartPage = (): ReactElement => {
    const {
        isLoading,
        isSilentLoading,
        activities,
        cartData,
        isExpired,
        initCart,
        removeItem,
        editTicket,
        expiredItems,
    } = useCartContext();
    const router = useRouter();

    const isCartTotal = !!cartData.total;

    const [removedLinks, setRemovedLinks] = useState<{ name: string; url: string }[]>([]);
    const [isContactInfoConfirmed, setIsContactInfoConfirmed] = useState(false);

    //const totalItems = getSavingsFromCartItems(cartData?.cart_items);

    useEffect(() => {
        initCart({ isCartPage: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                initCart({ silentUpdate: true });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* const onContinueShopping = useCallback(() => {
        push(
            createAttractionsUrl(
                selectedDestination?.slug,
                selectedDestination?.state.code,
                selectedCategory?.slug
            )
        );
    }, [selectedDestination, selectedCategory, push]); */

    const onRemoveItem = useCallback(
        async (item: IApiCartItem) => {
            removeItem(item);
            setRemovedLinks((prev) => [
                ...prev,
                { name: item.product_name, url: item.product_url },
            ]);
            try {
                const analyticsData = [
                    {
                        name: item.product_name,
                        id: item.product_id,
                        price: Number(item.tickets[0].price),
                        brand: item.partner_title,
                        category:
                            item.product_city_info.name + '|' + item.product_category_info.name,
                        variant: item.tickets[0].name,
                        quantity: Number(item.tickets[0].booked),
                    },
                ];
                AnalyticsInstance.product.removeFromCart(analyticsData);
            } catch (e) {
                console.log('GA3 remove cart item error!', e);
            }
        },
        [removeItem]
    );

    useEffect(() => {
        if (cartData.cart_items?.length) {
            trackUpdateCart(cartData.cart_items, activities);
        }
    }, [activities, cartData]);

    if (isLoading || isExpired) {
        return (
            <Layout noFooter>
                {isLoading && <Preloader />}
                {isExpired && !!expiredItems?.length && <ExpiredCart items={expiredItems} />}
            </Layout>
        );
    }

    if (!cartData.cart_items?.length && !isLoading && !removedLinks.length) {
        const hasExpiredItems = getLS(LocalStorageTypes.LS_CART_EXPIRED);

        if (hasExpiredItems && hasExpiredItems?.items?.length) {
            router.push('/expired-cart/');
        }

        return <CartSad isLoading={isSilentLoading} />;
    }

    const isTripflexItems = cartData.cart_items?.reduce((res, item) => {
        if (item.additional_options.find((option) => option.id === TRIPFLEX_ID)) {
            const key = item.product_id + item.schedule.id;

            res[key] = true;
        }

        return res;
    }, {} as { [key: string]: boolean });

    const noCartItems = !cartData.cart_items?.length;

    return (
        <Layout noFooter withMobileHeroImage>
            <CartPageMeta />
            <S.CartContainer>
                <S.Title>Your Booking</S.Title>
                {!!removedLinks.length && (
                    <S.RemovedWrapper>
                        {removedLinks.map((link, index) => {
                            return (
                                <S.RemovedItems key={link.url + index}>
                                    <Link href={link.url} passHref>
                                        <S.RemovedItem>{link.name}</S.RemovedItem>
                                    </Link>
                                    &nbsp; was removed from your cart
                                </S.RemovedItems>
                            );
                        })}
                    </S.RemovedWrapper>
                )}
                {!!cartData?.cart_items?.length && (
                    <>
                        {cartData.cart_items?.map((item) => {
                            {
                                return (
                                    <CartItem
                                        item={item}
                                        removeItem={onRemoveItem}
                                        editTicket={editTicket}
                                        activity={activities.find(
                                            (activity) => activity.id == item.product_id
                                        )}
                                        key={item.id}
                                        isTripflex={
                                            isTripflexItems?.[item.product_id + item.schedule.id]
                                        }
                                    />
                                );
                            }
                        })}

                        <PromocodeWidget />
                    </>
                )}
                {isCartTotal && (
                    <S.CartBottomWrapper id="cart-total-info">
                        <S.SubWrappers>
                            <S.SubWrapper>
                                Subtotal:
                                <div>${cartData.subtotal?.toFixed(2)}</div>
                            </S.SubWrapper>
                            {!!cartData.discount?.total && (
                                <S.SubWrapperPromo>
                                    Promo Code:
                                    <div>-{getNormalisedPrice(cartData.discount?.total)}</div>
                                </S.SubWrapperPromo>
                            )}
                            <S.SubWrapper>
                                <Tooltip
                                    description={
                                        <S.TipText>
                                            This small fee helps power TripShock&apos;s platform,
                                            ensuring you get access to the best activities,
                                            competitive pricing, and live 7-day support from our
                                            US-based team.
                                        </S.TipText>
                                    }
                                    inCart
                                >
                                    <S.Sur>
                                        Service Fee <Question />
                                    </S.Sur>
                                </Tooltip>
                                <div>${cartData.surcharges?.toFixed(2)}</div>
                            </S.SubWrapper>
                        </S.SubWrappers>
                        <S.GrandTotal id="cart_grand_total_bottom">
                            Grand Total:
                            <S.TotalPrice data-test-id="cart_grand_total_value">
                                ${cartData.total?.toFixed(2)}
                            </S.TotalPrice>
                        </S.GrandTotal>
                        <S.TotalDescription>
                            local & state taxes included where applicable
                        </S.TotalDescription>
                    </S.CartBottomWrapper>
                )}
                {!noCartItems && (
                    <>
                        <ShopperApprovedReviews />
                        <S.SubTitle>Step 1: Your Info</S.SubTitle>
                        <ContactInfo
                            isContactInfoConfirmed={isContactInfoConfirmed}
                            setIsContactInfoConfirmed={setIsContactInfoConfirmed}
                        />
                        <S.SubTitle>Step 2: Payment</S.SubTitle>
                        <BillingInfo isContactInfoConfirmed={isContactInfoConfirmed} />
                        <PayPalSection />
                    </>
                )}
            </S.CartContainer>

            {/* {isCartTotal && (
                <S.CartFooter>
                    <S.CartWrapper>
                        <S.FooterPrice>
                            Grand Total: &nbsp;
                            <S.FooterTotalPrice>${cartData.total?.toFixed(2)}</S.FooterTotalPrice>
                        </S.FooterPrice>
                        <S.FooterButtons>
                            <Button
                                variant="transparent"
                                onClick={onContinueShopping}
                                data-test-id="cart_continue_shopping"
                            >
                                Continue Shopping
                            </Button>
                            <Button
                                variant="primary"
                                onClick={submitContinue}
                                data-test-id="cart_expire_time_checkout"
                                styles="@media screen and (min-width: 500px) { margin-right: 0 !important }"
                            >
                                {isLoading ? <Spinner isLoading /> : CHECKOUT_NOW}
                            </Button>
                        </S.FooterButtons>
                    </S.CartWrapper>
                </S.CartFooter>
            )} */}
        </Layout>
    );
};

export { CartPage };
